import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import CompanyModal from './CompanyModal';

const { FiPlus, FiBriefcase, FiSearch, FiGlobe, FiPhone, FiDollarSign } = FiIcons;

const Companies = () => {
  const { companies } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  const filtered = companies.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Companies</h1>
          <p className="text-slate-400 font-medium">Manage corporate entities and billing relationships.</p>
        </div>
        <button onClick={() => { setEditingCompany(null); setIsModalOpen(true); }} className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all">
          <SafeIcon icon={FiPlus} /> New Company
        </button>
      </div>

      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
        <div className="relative w-full">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
          <input placeholder="Search companies..." className="w-full pl-14 pr-6 py-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filtered.map(company => (
            <motion.div key={company.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group cursor-pointer" onClick={() => { setEditingCompany(company); setIsModalOpen(true); }}>
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                  <SafeIcon icon={FiBriefcase} className="text-2xl" />
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rate</p>
                  <p className="font-black text-slate-900">${company.defaultHourlyRate}/h</p>
                </div>
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-2">{company.name}</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-slate-400 text-sm font-medium">
                  <SafeIcon icon={FiGlobe} /> {company.websiteUrl || 'No website'}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm font-medium">
                  <SafeIcon icon={FiPhone} /> {company.phone || 'No phone'}
                </div>
              </div>
              <div className="pt-6 border-t border-slate-50 flex justify-between items-center">
                <span className="bg-slate-50 text-slate-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{company.leadSource || 'Organic'}</span>
                <span className={`text-[10px] font-black uppercase tracking-widest ${company.ccFees === 'pass' ? 'text-green-600' : 'text-orange-600'}`}>CC: {company.ccFees}</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <CompanyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} company={editingCompany} />
    </div>
  );
};

export default Companies;